import { RootState } from "redux/store";
import { Badge, Modal } from "react-bootstrap";
import { useEffect, useState } from "react";
import { PopupTopBar, ProTable } from "components";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getStores, updateStoreField, addSelectedRows, cleanStoreDetails, addDeselectedRows } from "redux/slices/Stores";

type StoresPopupProps = {
	show: boolean;
	adminMode?: boolean;
	onClose: () => void;
	onSave: () => void;
}

export function StoresPopup(props: StoresPopupProps) {
	const dispatch = useAppDispatch();
	const [saving, setSaving] = useState(false);
	const stores = useAppSelector((state: RootState) => state.stores);
	const [counterSelectedRows, setCounterSelectedRows] = useState<number>(0);
	const currentStoreGroupId = useAppSelector((state: RootState) => state.global.storeGroupId);

	const adminModeColumns = [
		{
			Header: "Site ID",
			accessor: "id",
			defaultCanSort: true,
			width: 200,
		},
		{
			Header: 'Status',
			accessor: 'connected',
			width: 200,
			defaultCanSort: true,
			Cell: (row: any) => {
				return (
					<>
						{row.cell.row.original.connected === false && <Badge className="me-1 badge-outline-danger">Offline</Badge>}
						{row.cell.row.original.connected === true && <Badge className="me-1 badge-outline-primary">Online</Badge>}
					</>
				);
			}
		},
		{
			Header: "Running Version",
			accessor: "lastRunningVersion",
			defaultCanSort: true,
			width: 200,
		}
	];


	const columnsb = [
		{
			Header: "Name",
			accessor: "name",
			width: 500,
		},
		...(props.adminMode === true ? adminModeColumns : []),
	];

	useEffect(() => {
		if (props.show) {
			setSaving(false);
			dispatch(cleanStoreDetails());
			// Don't send keyword when popup is opened, since we want to search on a clean field.
			// Ideally, we would pass the keyword and it'd be cleared by this point but cleanStoreDetails() doesn't complete
			// before this getStores() call runs so it sends an old keyword
			dispatch(getStores(
				{
					contains: true,
					offset: 0,
					limit:100,
					...(currentStoreGroupId !== 0 && { storeGroupIds: [currentStoreGroupId] })
				}
			));
		}
	}, [props.show]);

	useEffect(() => {
		let rows = 0;
		if (!stores.selectedRows) setCounterSelectedRows(0);
		if (stores.selectAllFilter) {
			let deselectedRows = 0;
			stores.deselectedRows.forEach((row: any) => deselectedRows = deselectedRows + row.length);
			rows = stores.totalRows - deselectedRows;
		} else {
			stores.selectedRows.forEach((row: any) => rows = rows + row.length);
		}
		setCounterSelectedRows(rows);
	}, [stores.selectedRows, stores.deselectedRows]);

	useEffect(() => {
		columnCheckDeselectAll();
	}, [stores.pageSize]);

	function onSearchClick(keyword: string) {
		handleChange("keyword", keyword);
		dispatch(updateStoreField({ key: "pageIndex", value: 0 }));
		columnCheckDeselectAll();
		dispatch(getStores({
			keyword: keyword,
			contains: true,
			offset: 0,
			limit: stores.pageSize,
			...(currentStoreGroupId !== 0 && { storeGroupIds: [currentStoreGroupId] })
		}));
	}

	function handleChange(option: any, optionValue: any) {
		dispatch(updateStoreField({ key: option, value: optionValue }));
	}

	function updateTableRows(dataTableState: any) {
		dispatch(updateStoreField({ key: "pageIndex", value: dataTableState.pageIndex }));
		dispatch(updateStoreField({ key: "pageSize", value: dataTableState.pageSize }));
		dispatch(getStores({
			keyword: stores.keyword,
			contains: true,
			limit: dataTableState.pageSize,
			offset: dataTableState.pageIndex * dataTableState.pageSize,
			...(currentStoreGroupId !== 0 && { storeGroupIds: [currentStoreGroupId] })
		}));
	}

	function deselectedRowsUpdate(rows: any, index: any) {
		dispatch(addDeselectedRows({ rows: rows, index: index }));
	}

	function selectedRowsUpdate(rows: any, index: any) {
		dispatch(addSelectedRows({ rows: rows, index: index }));
	}

	function columnCheckDeselectAll() {
		handleChange("selectAllFilter", false);
		handleChange("deselectedRows", []);
		handleChange("selectedRows", []);
	}

	function columnCheckSelectAll() {
		handleChange("selectAllFilter", true);
		handleChange("deselectedRows", []);
	}

	function saveSelectedRows() {
		setSaving(true);
		props.onSave();
	}

	return (
		<Modal size="lg" centered show={props.show} onHide={props.onClose}>
			<Modal.Header closeButton>
				<Modal.Title>Stores</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<ProTable
					data={stores}
					pageSize={100}
					isSortable={true}
					pagination={true}
					disableExport={true}
					columns={columnsb}
					table={stores.table}
					loading={stores.loading}
					updateTableRows={updateTableRows}
					// Search Props
					isSearchable={true}  
					onSearchClick={onSearchClick}
					// Select Props
					isSelectable={true}
					selectAllFilter={stores.selectAllFilter}
					selectedRows={stores.selectedRows}
					deselectedRows={stores.deselectedRows}
					selectedRowsUpdate={selectedRowsUpdate}
					deselectedRowsUpdate={deselectedRowsUpdate}
					columnCheckSelectAll={columnCheckSelectAll}
					columnCheckDeselectAll={columnCheckDeselectAll}
					// Extra Components Props
					topbarChild={<PopupTopBar counter={counterSelectedRows} onSave={saveSelectedRows} saving={saving} />}
				/>
			</Modal.Body>
		</Modal>
	);
}